import { useEffect } from "react";
import { useTranslation as useI18nextTranslation } from "react-i18next";
import { initPrimeReactLocale } from "./primeReactLocale";

/**
 * Enhanced useTranslation hook that also syncs PrimeReact locale
 */
export const useTranslation = (ns?: string | string[]) => {
	const translation = useI18nextTranslation(ns);

	useEffect(() => {
		// Sync PrimeReact locale when language changes
		initPrimeReactLocale(translation.i18n.language);
	}, [translation.i18n.language]);

	return translation;
};

/**
 * Hook specifically for tree-related translations
 * Provides commonly used tree translation keys with proper typing
 */
export const useTreeTranslation = () => {
	const { t } = useTranslation("tree");

	return {
		t,
		tree: t("tree"),
		treePlural: t("treePlural"),
		thisTree: t("thisTree"),
		contentNode: t("contentNode"),
		thisContentNode: t("thisContentNode"),
		thisContentNodeUppercase: t("thisContentNodeUppercase"),
	};
};

/**
 * Hook for validation messages
 * Returns functions to get validation messages with proper interpolation
 */
export const useValidationTranslation = () => {
	const { t } = useTranslation("validation");

	return {
		required: () => t("required"),
		email: () => t("email"),
		minLength: (min: number) => t("minLength", { min }),
		maxLength: (max: number) => t("maxLength", { max }),
		min: (min: number) => t("min", { min }),
		max: (max: number) => t("max", { max }),
		pattern: () => t("pattern"),
		passwordMatch: () => t("passwordMatch"),
	};
};

/**
 * Hook for error messages
 */
export const useErrorTranslation = () => {
	const { t } = useTranslation("errors");

	return {
		getErrorMessage: (errorKey: string, fallback = "generic") => {
			return t(errorKey, { defaultValue: t(fallback) });
		},
	};
};
